/**
 * Composant de sélection de dates (De ... à ...)
 */

import { html, unsafeCSS, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import datePickerStyles from "../styles/date-picker.css?inline";
import { WidgetLanguage } from "../types/widgets.d";
import { TranslationService } from "../services/TranslationService";
import { ApidaeVisualHost } from "./ApidaeVisualHost";

export interface DateRange {
  dateDebut: string | null;
  dateFin: string | null;
}

@customElement("apidae-date-picker")
export class DatePicker extends ApidaeVisualHost {
  static styles = unsafeCSS(datePickerStyles);

  @property({ type: Object })
  value: DateRange = { dateDebut: null, dateFin: null };

  @property({ type: String })
  language: WidgetLanguage = WidgetLanguage.FR;

  @state()
  private dateDebut: string = "";

  @state()
  private dateFin: string = "";

  connectedCallback() {
    super.connectedCallback();
    if (
      typeof navigator !== "undefined" &&
      /firefox/i.test(navigator.userAgent)
    ) {
      this.classList.add("date-picker--firefox");
    }
  }
  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has("value")) {
      this.dateDebut = this.value.dateDebut || "";
      this.dateFin = this.value.dateFin || "";
    }
  }

  private handleDateDebutChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const newDateDebut = target.value;

    // Si une date de fin existe et qu'elle est antérieure à la nouvelle date de début, la réinitialiser
    if (newDateDebut && this.dateFin && newDateDebut > this.dateFin) {
      this.dateFin = "";
    }

    this.dateDebut = newDateDebut;
    this.emitChange();
  }

  private handleDateFinChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const newDateFin = target.value;

    // Empêcher de mettre une date de fin antérieure à la date de début
    if (newDateFin && this.dateDebut && newDateFin < this.dateDebut) {
      // Réinitialiser la date de fin si elle est antérieure à la date de début
      target.value = "";
      return;
    }

    this.dateFin = newDateFin;
    this.emitChange();
  }

  private handleKeyDown(e: KeyboardEvent) {
    // Empêcher la saisie manuelle au clavier, mais permettre le picker natif
    // Autoriser les touches de navigation et de suppression
    const allowedKeys = [
      "Tab",
      "Enter",
      "Escape",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Delete",
      "Backspace",
    ];
    // Si ce n'est pas une touche autorisée et que ce n'est pas un raccourci (Ctrl, Alt, Meta)
    if (!allowedKeys.includes(e.key) && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
    }
  }

  private handlePaste(e: ClipboardEvent) {
    // Empêcher le collage de texte
    e.preventDefault();
  }

  private emitChange() {
    this.dispatchEvent(
      new CustomEvent("date-change", {
        detail: {
          dateDebut: this.dateDebut || null,
          dateFin: this.dateFin || null,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleInputClick(e: Event) {
    // Permettre d'ouvrir le picker en cliquant n'importe où sur l'input
    const target = e.target as HTMLInputElement;
    if (target && target.type === "date") {
      target.showPicker?.();
    }
  }

  /** Icône calendrier (couleur via .date-calendar-icon { color: var(--widget-text-color) }) */
  private renderCalendarIcon() {
    return html`<span class="date-calendar-icon" aria-hidden="true">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
      >
        <path
          fill="currentColor"
          d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"
        />
      </svg>
    </span>`;
  }

  private getLocaleFromLanguage(): string {
    const localeMap: Record<WidgetLanguage, string> = {
      [WidgetLanguage.FR]: "fr-FR",
      [WidgetLanguage.EN]: "en-US",
      [WidgetLanguage.ES]: "es-ES",
      [WidgetLanguage.IT]: "it-IT",
      [WidgetLanguage.DE]: "de-DE",
      [WidgetLanguage.NL]: "nl-NL",
      [WidgetLanguage.RU]: "ru-RU",
      [WidgetLanguage.ZH]: "zh-CN",
      [WidgetLanguage.PT_BR]: "pt-BR",
      [WidgetLanguage.JA]: "ja-JP",
      [WidgetLanguage.KO]: "ko-KR",
    };
    return localeMap[this.language] || "fr-FR";
  }

  render() {
    const locale = this.getLocaleFromLanguage();
    const dateFrom = TranslationService.translate("dateFrom", this.language);
    const dateTo = TranslationService.translate("dateTo", this.language);
    const clearDateStart = TranslationService.translate(
      "clearDateStart",
      this.language,
    );
    const clearDateEnd = TranslationService.translate(
      "clearDateEnd",
      this.language,
    );

    return html`
      <div class="date-picker">
        <label class="date-label">${dateFrom}</label>
        <div class="date-input-wrapper">
          <input
            type="date"
            class="date-input"
            .value=${this.dateDebut}
            lang=${locale}
            @change=${this.handleDateDebutChange}
            @click=${this.handleInputClick}
            @keydown=${this.handleKeyDown}
            @paste=${this.handlePaste}
          />
          ${this.renderCalendarIcon()}
          ${this.dateDebut
            ? html`
                <button
                  class="date-clear-button"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    this.dateDebut = "";
                    this.emitChange();
                  }}
                  aria-label=${clearDateStart}
                >
                  ×
                </button>
              `
            : ""}
        </div>
        <label class="date-label">${dateTo}</label>
        <div class="date-input-wrapper">
          <input
            type="date"
            class="date-input"
            .value=${this.dateFin}
            .min=${this.dateDebut || undefined}
            lang=${locale}
            @change=${this.handleDateFinChange}
            @click=${this.handleInputClick}
            @keydown=${this.handleKeyDown}
            @paste=${this.handlePaste}
          />
          ${this.renderCalendarIcon()}
          ${this.dateFin
            ? html`
                <button
                  class="date-clear-button"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    this.dateFin = "";
                    this.emitChange();
                  }}
                  aria-label=${clearDateEnd}
                >
                  ×
                </button>
              `
            : ""}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-date-picker": DatePicker;
  }
}
